import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { 
  useMediaQuery, 
  Box, 
  Typography, 
  Card, 
  CardContent,
  Button,
  Chip,
  Stack
} from '@mui/material';
import {
  Info as InfoIcon,
  LocationOn as LocationOnIcon,
  LocalShipping as TruckIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import './RouteMap.css';
import busyTruck from '../assets/img/busyTruck.png';
import { useGoogleMaps } from '../utils/GoogleMapsProvider';

const RouteMap = (props) => {
  const [activeMarker, setActiveMarker] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState({});
  const [mapLoaded, setMapLoaded] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const mapRef = useRef(null);
  const boundsTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Extract isModal prop
  const { isModal = false } = props;

  // Responsive breakpoints
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');
  const isSmallMobile = useMediaQuery('(max-width:400px)');

  // Enhanced container styling with modal support
  const getContainerStyle = () => {
    // If in modal, use calc to fill remaining space
    if (isModal) {
      return {
        width: '100%',
        height: 'calc(100vh - 120px)', // Adjust based on header height
        borderRadius: isMobile ? '16px' : '12px',
        overflow: 'hidden',
        boxShadow: isMobile 
          ? '0 8px 32px rgba(0,0,0,0.12)' 
          : '0 4px 20px rgba(0,0,0,0.08)',
        position: 'relative',
      };
    }
    
    // Keep existing logic for non-modal usage
    const baseHeight = isMobile 
      ? isSmallMobile ? '350px' : '400px'
      : isTablet ? '500px' : '600px';

    return {
      width: '100%',
      height: baseHeight,
      borderRadius: isMobile ? '16px' : '12px',
      overflow: 'hidden',
      boxShadow: isMobile 
        ? '0 8px 32px rgba(0,0,0,0.12)' 
        : '0 4px 20px rgba(0,0,0,0.08)',
      position: 'relative',
    };
  };

  const center = {
    lat: 33.429078,
    lng: -111.943817,
  };

  // Use shared Google Maps provider
  const { isLoaded, loadError } = useGoogleMaps();

  // Enhanced Google Maps API readiness check
  const checkGoogleMapsReady = useCallback(() => {
    return !!(
      window.google && 
      window.google.maps && 
      window.google.maps.LatLngBounds && 
      window.google.maps.LatLng &&
      window.google.maps.Size
    );
  }, []);

  // Monitor API readiness
  useEffect(() => {
    if (isLoaded) {
      const checkReady = () => {
        if (checkGoogleMapsReady()) {
          setApiReady(true);
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    }
  }, [isLoaded, checkGoogleMapsReady]);

  // Safe bounds fitting with mobile optimization
  useEffect(() => {
    if (apiReady && mapLoaded && mapRef.current && props.data && props.data.length > 0) {
      if (boundsTimeoutRef.current) {
        clearTimeout(boundsTimeoutRef.current);
      }
      
      boundsTimeoutRef.current = setTimeout(() => {
        fitBoundsToMarkers();
      }, isModal ? 500 : 200); // Longer delay for modal
    }
    
    return () => {
      if (boundsTimeoutRef.current) {
        clearTimeout(boundsTimeoutRef.current);
      }
    };
  }, [props.data, apiReady, mapLoaded, isModal]);

  const isValidLatLng = (lat, lng) => {
    return typeof lat === 'number' && !isNaN(lat) && typeof lng === 'number' && !isNaN(lng) &&
           lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  };

  // Enhanced bounds fitting for modal context
  const fitBoundsToMarkers = useCallback(() => {
    if (!mapRef.current || !checkGoogleMapsReady() || !props.data || props.data.length === 0) {
      return;
    }

    try {
      const bounds = new window.google.maps.LatLngBounds();
      let validLocationsCount = 0;

      props.data.forEach(location => {
        if (isValidLatLng(location?.coordinates?.lat, location?.coordinates?.lng)) {
          bounds.extend(new window.google.maps.LatLng(
            location.coordinates.lat, 
            location.coordinates.lng
          ));
          validLocationsCount++;
        }
      });

      if (validLocationsCount > 0) {
        // Enhanced padding for modal context
        const padding = isModal 
          ? (isMobile ? 80 : 100) 
          : (isMobile ? (isSmallMobile ? 40 : 60) : 50);
        
        mapRef.current.fitBounds(bounds, padding);
        
        // Additional zoom adjustment for modal
        setTimeout(() => {
          if (mapRef.current && validLocationsCount === 1) {
            mapRef.current.setZoom(isModal ? 14 : 15);
          } else if (mapRef.current && validLocationsCount === 2) {
            mapRef.current.setZoom(isModal ? 12 : 13);
          } else if (mapRef.current && validLocationsCount >= 3) {
            mapRef.current.setZoom(isModal ? 11 : 12);
          }
        }, 500); // Increased delay for modal rendering
      }
    } catch (error) {
      console.warn('Error fitting bounds to markers:', error);
      if (mapRef.current) {
        mapRef.current.setCenter(center);
        mapRef.current.setZoom(isMobile ? 12 : 10);
      }
    }
  }, [props.data, isModal, isMobile, isSmallMobile, checkGoogleMapsReady]);

  const getIconForAddressType = useCallback((addressType) => {
    if (!checkGoogleMapsReady()) {
      return null;
    }

    try {
      const icons = {
        Pickup: 'https://maps.google.com/mapfiles/kml/paddle/red-circle.png',
        Drop: 'https://maps.google.com/mapfiles/kml/paddle/grn-circle.png',
        Truck: busyTruck,
      };
      
      // Enhanced mobile-responsive icon sizing
      const iconSize = isSmallMobile ? 24 : isMobile ? 28 : isTablet ? 32 : 36;
      
      return {
        url: icons[addressType] || 'https://maps.google.com/mapfiles/kml/paddle/grn-circle.png',
        scaledSize: new window.google.maps.Size(iconSize, iconSize),
        anchor: new window.google.maps.Point(iconSize/2, iconSize/2),
      };
    } catch (error) {
      console.warn('Error creating marker icon:', error);
      return null;
    }
  }, [checkGoogleMapsReady, isMobile, isSmallMobile, isTablet]);

  const onMarkerClick = useCallback((location, index) => {
    if (!location || !location.coordinates) return;
    
    try {
      setSelectedPlace(location);
      setActiveMarker({ 
        lat: location.coordinates.lat, 
        lng: location.coordinates.lng 
      });
    } catch (error) {
      console.warn('Error handling marker click:', error);
    }
  }, []);

  const onMapClick = useCallback(() => {
    setActiveMarker(null);
  }, []);

  const getAddressTypeIcon = (type) => {
    switch(type) {
      case 'Pickup': return <LocationOnIcon sx={{ color: '#f44336' }} />;
      case 'Drop': return <HomeIcon sx={{ color: '#4caf50' }} />;
      case 'Truck': return <TruckIcon sx={{ color: '#ff9800' }} />;
      default: return <LocationOnIcon />;
    }
  };

  // Enhanced onLoad with modal support
  const onLoad = useCallback((map) => {
    mapRef.current = map;
    setMapLoaded(true);
    
    // Longer delay for modal context to ensure proper rendering
    const delay = isModal ? 800 : 250;
    
    setTimeout(() => {
      if (apiReady && props.data && props.data.length > 0) {
        fitBoundsToMarkers();
      }
    }, delay);
  }, [apiReady, props.data, isModal, fitBoundsToMarkers]);

  // Enhanced loading state with better mobile design
  if (loadError) {
    return (
      <Card 
        sx={{ 
          height: isModal ? '100%' : (isMobile ? '350px' : '600px'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffebee',
          borderRadius: isMobile ? '16px' : '12px',
          margin: isMobile ? '8px 0' : '0'
        }}
      >
        <CardContent sx={{ textAlign: 'center', p: isMobile ? 2 : 3 }}>
          <Typography variant={isMobile ? "h6" : "h5"} color="error" gutterBottom>
            🗺️ Map Loading Error
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Unable to load Google Maps. Please check your connection and try again.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
            size={isMobile ? "small" : "medium"}
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!isLoaded) {
    return (
      <Card 
        sx={{ 
          height: isModal ? '100%' : (isMobile ? '350px' : '600px'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: isMobile ? '16px' : '12px',
          margin: isMobile ? '8px 0' : '0'
        }}
      >
        <CardContent sx={{ textAlign: 'center', p: isMobile ? 2 : 3 }}>
          <Box 
            sx={{ 
              width: isMobile ? 40 : 50, 
              height: isMobile ? 40 : 50,
              border: '4px solid #e3f2fd',
              borderTop: '4px solid #2196f3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px auto'
            }}
          />
          <Typography variant={isMobile ? "h6" : "h5"} color="primary" gutterBottom>
            Loading Maps...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preparing your route visualization
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box ref={containerRef} sx={{ position: 'relative', height: isModal ? '100%' : 'auto' }}>
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      
      <GoogleMap
        mapContainerStyle={getContainerStyle()}
        center={center}
        zoom={props.zoom || (isMobile ? 13 : 10)}
        onClick={onMapClick}
        onLoad={onLoad}
        options={{
          // Enhanced zoom controls
          zoomControl: true,
          zoomControlOptions: {
            position: window.google?.maps?.ControlPosition?.RIGHT_CENTER || 3,
            style: window.google?.maps?.ZoomControlStyle?.DEFAULT || 0
          },
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: !isModal, // Disable fullscreen in modal
          gestureHandling: isMobile ? 'greedy' : 'cooperative',
          clickableIcons: true,
          mapTypeId: 'roadmap'
        }}
      >
        {/* Render markers when everything is ready */}
        {apiReady && mapLoaded && props.data && props.data.map((location, index) => {
          if (!location || 
              !location.coordinates || 
              !isValidLatLng(location.coordinates.lat, location.coordinates.lng) || 
              !location.address) {
            return null;
          }

          const icon = getIconForAddressType(location.addressType);

          return (
            <Marker
              key={index}
              position={{ 
                lat: location.coordinates.lat, 
                lng: location.coordinates.lng 
              }}
              icon={icon}
              onClick={() => onMarkerClick(location, index)}
              title={`${location.addressType}: ${location.address}`}
              animation={isMobile ? null : window.google?.maps?.Animation?.DROP}
            />
          );
        })}
        
        {/* Enhanced Info Window - works for both mobile and desktop */}
        {activeMarker && selectedPlace && (
          <InfoWindow
            position={activeMarker}
            onCloseClick={() => setActiveMarker(null)}
            options={{
              maxWidth: isMobile ? 280 : 320,
              pixelOffset: new window.google.maps.Size(0, -5),
            }}
          >
            <Box sx={{ p: 1, minWidth: isMobile ? 200 : 250 }}>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getAddressTypeIcon(selectedPlace.addressType)}
                  <Typography 
                    variant={isMobile ? "subtitle2" : "h6"} 
                    sx={{ fontWeight: 'bold', flex: 1 }}
                  >
                    {selectedPlace.addressType}
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: isMobile ? '12px' : '14px',
                    lineHeight: 1.4,
                    wordBreak: 'break-word'
                  }}
                >
                  📍 {(selectedPlace.address || '').replace(/null/g, '').replace(/\s+/g, ' ').trim() || 'Address not available'}
                </Typography>
                {selectedPlace.eta && (
                  <Chip 
                    label={`ETA: ${selectedPlace.eta}`} 
                    color="primary" 
                    size="small"
                    icon={<InfoIcon />}
                    sx={{ fontSize: isMobile ? '11px' : '12px' }}
                  />
                )}
              </Stack>
            </Box>
          </InfoWindow>
        )}
      </GoogleMap>
    </Box>
  );
};

export default RouteMap;